/**
 * Modèle de rôles IT Solutions (cf. cahier des charges — gestion des droits).
 * Les 6 groupes d'utilisateurs de l'outil de gestion de projets.
 */
export const ROLES = [
    'direction',
    'sponsor',
    'chef_projet',
    'membre',
    'testeur',
    'comex',
];
export const ROLE_LABELS = {
    direction: 'Direction',
    sponsor: 'Sponsor',
    chef_projet: 'Chef de projet',
    membre: 'Membre',
    testeur: 'Testeur',
    comex: 'Comex',
};
export function isRole(value) {
    return typeof value === 'string' && ROLES.includes(value);
}
const has = (roles, ...allowed) => roles.some((r) => allowed.includes(r));
/*
 * Droits du Module 1 « Plateforme de gestion des plans d'actions », d'après
 * l'Excel « gestion des droits » joint au CDC. Périmètre STRICT de ce document :
 * il ne dit rien des modules 2 et 3, où Testeur et Comex conservent donc les
 * droits d'un membre.
 *
 * Deux enseignements de la matrice :
 *  - le Testeur n'a AUCUN droit sur le Module 1 (son rôle vise le Module 4,
 *    « Tests – Recette – Run », non construit) ;
 *  - le Comex est un profil CONSULTATIF : il visualise les plans, sans rien
 *    créer ni modifier.
 */
/*
 * DEPUIS LA BASCULE (0045), `sponsor` et `chef_projet` ne figurent plus dans
 * `app_user.roles` : ils se lisent sur le projet. Les listes ci-dessous ne les
 * mentionnent donc plus — les y laisser aurait entretenu l'illusion qu'un rôle
 * global existe encore, alors qu'aucun compte n'en porte.
 *
 * Le droit ne se réduit pas pour autant : `membre` est désormais le SOCLE que
 * tout compte actif porte, et il ouvre la visualisation comme la création.
 */
/** « Visualisation des plans d'actions » — tous sauf le Testeur. */
export const M1_VIEW_ROLES = ['direction', 'membre', 'comex'];
/** « Création d'actions » — le socle suffit (ligne de la matrice du CDC). */
export const M1_ACTION_CREATE_ROLES = ['direction', 'membre'];
/**
 * « Indicateurs de suivi de TOUTES les actions » et « Modifications de toutes
 * les actions ». Le Membre, lui, ne voit et ne modifie QUE les siennes.
 */
export const M1_ACTION_ALL_ROLES = ['direction'];
/** Dépôt d'idées : les internes, hors Testeur et Comex. */
export const IDEA_ROLES = ['direction', 'membre'];
/**
 * Consultation des ARCHIVES (plans, projets, actions et idées archivés) —
 * arbitrage 2026-08-17. Ce qui est archivé sort des écrans courants au lieu d'y
 * traîner avec une pastille grise ; il reste consultable dans un onglet dédié,
 * réservé aux profils de pilotage. Un Membre ou un Comex ne voit donc plus
 * l'archivé du tout : c'est de l'historique, pas de l'actualité.
 */
export const M1_ARCHIVE_ROLES = ['direction'];
export function canViewActionPlans(roles) {
    return has(roles, ...M1_VIEW_ROLES);
}
/**
 * « Création de plans d'actions » — Direction seule (matrice, et CDC p.2 :
 * « seule la direction peut créer des plans d'actions »).
 */
export function canManageActionPlans(roles) {
    return roles.includes('direction');
}
/**
 * « Création d'actions » — Direction, Sponsor, Chef de projet ET Membres.
 *
 * La matrice fait autorité sur les droits (arbitrage du 2026-07-24) : elle
 * l'emporte sur la formulation du CDC p.3 « les membres sont obligés de passer
 * par la phase d'idéation ». L'idéation reste évidemment disponible.
 */
export function canCreatePlanActions(roles) {
    return has(roles, ...M1_ACTION_CREATE_ROLES);
}
/**
 * « Modifications de TOUTES les actions » et « Indicateurs de suivi de toutes
 * les actions ». Un Membre n'a que « mes actions » : il ne voit et ne modifie
 * que celles qu'il a créées ou dont il est le réalisateur.
 */
export function canManageAllPlanActions(roles) {
    return has(roles, ...M1_ACTION_ALL_ROLES);
}
/**
 * L'idéation reste ouverte aux collaborateurs internes — chacun dépose,
 * modifie ou supprime SES propres idées. Testeur et Comex en sont exclus :
 * la matrice ne leur accorde aucun droit de création sur le Module 1.
 */
export function canSubmitIdeas(roles) {
    return has(roles, ...IDEA_ROLES);
}
/** Accès à l'onglet « Archives » des plans, du détail de plan et de l'idéation. */
export function canViewArchives(roles) {
    return has(roles, ...M1_ARCHIVE_ROLES);
}
/*
 * Droits de la MISE EN PRODUCTION (Tests – Recette – Run) — refonte du
 * 2026-09-01.
 *
 * CE QUI A CHANGÉ, ET POURQUOI. Le suivi de mise en production avait son propre
 * module, sa propre entrée de menu, et une consultation « transverse » ouverte à
 * tout compte. Constat du donneur d'ordre : « il n'apporte rien hormis de la
 * confusion ». Un chef de projet, un testeur, y trouvaient un pilotage
 * Tests·Recette·Run dont ils n'avaient ni l'usage ni le droit d'agir, et qui
 * disait pourtant leur nom de projet en gros.
 *
 * LA LIVRAISON APPARTIENT À CEUX QUI LIVRENT. Le dossier se pilote désormais
 * depuis le module « Développement » : la FONCTION `developpeur`, et elle seule
 * depuis le 2026-09-25 (la Direction en est sortie avec le module entier, cf.
 * `canViewDevModule`). Ce n'est plus un droit DE PROJET —
 * d'où la disparition de `canViewProduction` et de `M4_VIEW_ROLES`, qui
 * n'excluaient personne et ne servaient donc à rien.
 *
 * CE QUI RESTE UN DROIT DE PROJET : la phase Tests, terrain du TESTEUR étiqueté
 * sur le projet (0038). C'est le seul point d'entrée de « Tests et
 * améliorations », et il ne bouge pas.
 */
/**
 * PILOTER une mise en production : ouvrir le dossier, renseigner les phases,
 * les valider, fermer ou rouvrir.
 *
 * Volontairement GLOBAL et non par projet. La fonction `developpeur` l'est déjà
 * par décision (cf. `canViewDevModule`), et c'est elle qui porte le sens : ceux
 * qui construisent et déploient tiennent le dossier de livraison, quel que soit
 * le projet.
 */
export function canPilotProduction(roles, jobFunctionCode) {
    return canViewDevModule(roles, jobFunctionCode);
}
/**
 * AGIR SUR UNE PHASE — préparer ses points de contrôle, la démarrer, la valider.
 *
 *  - qui pilote la mise en production agit sur les trois phases ;
 *  - le TESTEUR du projet agit sur la sienne, la phase Tests.
 *
 * Le testeur prépare aussi les points de Recette et de Run (c'est le geste que
 * `mayTest` autorise côté API, sur la phase Tests prise comme référence) ; ce
 * qu'il ne fait pas, c'est VALIDER une phase qui n'est pas la sienne.
 *
 * ⚠ AUCUN ÉCRAN NE LE LUI OFFRE PLUS depuis le 2026-09-08 : son espace ne sert
 * plus qu'à remonter ce qu'il trouve, la campagne étant passée à ceux qui
 * livrent. Le droit n'a pas été retiré pour autant — la demande portait sur ce
 * que le testeur VOIT, pas sur ce que l'API autorise, et la phase Tests reste
 * une phase DE PROJET. C'est ici qu'il faudra couper le jour où l'on tranchera
 * qu'un testeur ne touche plus jamais un point de contrôle.
 */
export function canActOnProductionPhase(kind, standing, jobFunctionCode) {
    if (canPilotProduction(standing.globalRoles, jobFunctionCode))
        return true;
    return kind === 'tests' && standing.projectRoles.includes('testeur');
}
/*
 * Droits sur les Modules 2 (gestion de projets) et 3 (plan de charge) —
 * arbitrage 2026-07-31 : le TESTEUR et le COMEX y passent en CONSULTATION SEULE
 * (ils voient tout, mais ne créent/modifient rien au titre de ces seuls rôles).
 * Leur terrain opérationnel est ailleurs : le Testeur en Module 4 (phase Tests),
 * le Comex en pilotage transverse. Un collaborateur qui cumule un rôle
 * opérationnel (Membre/CP/Sponsor/Direction) garde évidemment ses droits.
 */
export const M2_ACTOR_ROLES = ['direction', 'membre'];
/** Peut AGIR sur le Module 2 (créer/modifier). Comex/Testeur seuls : non. */
export function canActOnModule2(roles) {
    return has(roles, ...M2_ACTOR_ROLES);
}
/* ================================================================== */
/* Rôles à l'échelle du PROJET — bascule du point 6                    */
/* ================================================================== */
/**
 * DÉCISIONS VERROUILLÉES le 2026-08-19, après cartographie
 * (`docs/CARTOGRAPHIE-ROLES.md`) :
 *
 *  1. Restent GLOBAUX : `direction` et `comex`, plus la FONCTION
 *     `developpeur` (qui n'est pas un rôle et vit sur `job_function`).
 *  2. `membre` devient un SOCLE DE COMPTE et non un rôle de projet — sans
 *     lui, un collaborateur affecté à aucun projet perdrait jusqu'à
 *     l'idéation, que l'arbitrage veut ouverte à tous.
 *  3. Les rôles sont PAR PROJET. Le Module 1 en dérive : aucun rôle n'est
 *     porté par le plan.
 *  4. Reprise : un `chef_projet` global sans projet redevient simple membre.
 *  5. Livraison en trois temps ; ceci est le TEMPS 1, ajout pur.
 */
/** Rôles qui n'ont de sens QUE globalement : ils arbitrent le portefeuille. */
export const GLOBAL_ROLES = ['direction', 'comex'];
/**
 * Rôles qui prennent leur sens SUR UN PROJET.
 *
 * `membre` n'y figure pas (décision 2) : appartenir à un projet se lit dans
 * `project_member`, ce n'est pas un rôle. Et ces trois-là sont DÉJÀ portés en
 * base — `project.sponsor_id`, `project.chef_projet_id`,
 * `project_member.is_tester` (0038) — d'où l'absence de table nouvelle.
 */
export const PROJECT_ROLES = ['sponsor', 'chef_projet', 'testeur'];
/**
 * Rôles ATTRIBUABLES depuis l'Administration, sur un compte.
 *
 * `sponsor`, `chef_projet` et `testeur` n'y figurent plus : ils se donnent en
 * désignant quelqu'un SUR UN PROJET (sponsor, chef de projet, étiquette
 * testeur). Les proposer ici permettrait de recréer par la fenêtre les rôles
 * globaux qu'on vient de retirer par la porte.
 */
export const ASSIGNABLE_ROLES = ['direction', 'comex', 'membre'];
export const isGlobalRole = (role) => GLOBAL_ROLES.includes(role);
export const isProjectRole = (role) => PROJECT_ROLES.includes(role);
/**
 * PILOTER un projet : en fixer les dates, les affectations, le budget, la
 * clôture. C'est le droit que porte aujourd'hui la combinaison
 * `direction, sponsor, chef_projet`, ramenée à l'échelle du projet.
 *
 * La Direction pilote partout — c'est le sens même de son rôle global.
 */
export function canPilotProject(standing) {
    if (standing.globalRoles.includes('direction'))
        return true;
    return standing.projectRoles.some((r) => r === 'sponsor' || r === 'chef_projet');
}
/**
 * DIRE QU'UN PROJET LIVRE DU LOGICIEL (0076) — poser ou retirer le marqueur qui
 * l'ouvre au module Développement.
 *
 * Deux publics, parce que deux savoirs : les PILOTES savent ce que le projet
 * doit produire, les DÉVELOPPEURS savent qu'ils auront à le livrer. Ce n'est pas
 * un arbitrage de portefeuille comme la requalification en mission : le
 * réserver à la Direction l'aurait laissée seule à deviner quels projets
 * contiennent du code.
 */
export function canMarkDevelopment(standing, jobFunctionCode) {
    return canPilotProject(standing) || canPilotProduction(standing.globalRoles, jobFunctionCode);
}
/**
 * La position sur un projet DÉJÀ CHARGÉ, sans repasser par l'API.
 *
 * Sert aux LISTES — tuiles de projets, tableau de bord — où le droit diffère
 * d'une ligne à l'autre et où interroger `/my-standing` par ligne ferait autant
 * d'allers-retours qu'il y a de projets. Tout ce qu'il faut est déjà dans la
 * charge utile : qui parraine, qui pilote, qui est membre.
 *
 * La DÉCISION, elle, n'est pas dupliquée : on rend un `ProjectStanding` que
 * `canPilotProject` et consorts tranchent comme partout ailleurs. Réécrire la
 * règle côté écran, c'est se condamner à la voir diverger du serveur — ce qui
 * vient précisément d'arriver avec les rôles globaux `sponsor` / `chef_projet`,
 * restés dans six écrans après leur retrait des comptes (0045).
 *
 * Une seule nuance à connaître : l'étiquette « testeur » ne voyage pas toujours
 * dans les listes ; ce raccourci ne sert donc qu'à juger du PILOTAGE et de la
 * contribution, jamais des droits du Module 4, qui gardent `/my-standing`.
 */
export function standingOnLoadedProject(project, userId, globalRoles) {
    const projectRoles = [];
    if (userId && project.sponsorId === userId)
        projectRoles.push('sponsor');
    if (userId && project.chefProjetId === userId)
        projectRoles.push('chef_projet');
    // Sponsor et chef de projet sont membres DE DROIT (cf. lib/projectRoles côté API).
    const isMember = projectRoles.length > 0 || Boolean(userId && project.members?.some((m) => m.id === userId));
    return {
        projectRoles,
        isMember,
        globalRoles: globalRoles.filter((r) => GLOBAL_ROLES.includes(r)),
    };
}
/**
 * CONTRIBUER : faire avancer le travail du projet (avancement, statut,
 * descriptif, commentaires). Correspond à `direction, sponsor, chef_projet,
 * membre`, ramené au projet.
 *
 * Le Comex, lui, consulte sans agir — arbitrage 2026-07-31 inchangé : porter
 * `comex` ne suffit pas, il faut être du projet.
 */
export function canContributeToProject(standing) {
    return canPilotProject(standing) || standing.isMember;
}
/**
 * VOIR un projet. La Direction et le Comex voient tout — le premier arbitre le
 * portefeuille, le second est un profil consultatif transverse.
 */
export function canViewProject(standing) {
    return (standing.globalRoles.some((r) => r === 'direction' || r === 'comex') ||
        canContributeToProject(standing));
}
/* ================================================================== */
/* Module 5 — Développement (0052)                                     */
/* ================================================================== */
/*
 * CE MODULE S'OUVRE SUR UNE FONCTION, PAS SUR UN RÔLE.
 *
 * La demande le dit telle quelle : « un module propre aux personnes ayant la
 * fonction développeur ». Et le modèle sait déjà faire la différence — la
 * décision 1 de la bascule des rôles (0045) range explicitement `developpeur`
 * du côté de `job_function`, hors des rôles. Créer un rôle « développeur »
 * pour l'occasion aurait rouvert la porte que cette bascule venait de fermer :
 * un huitième rôle global, attribuable à côté du métier, et donc capable d'en
 * diverger.
 *
 * Conséquence pratique : ces fonctions prennent un `jobFunctionCode` en plus
 * des rôles. C'est le même couple que porte déjà l'identité (`Identity`), et
 * que le menu consulte pour « Mise en production ».
 */
/**
 * Voir le module — LA FONCTION DÉVELOPPEUR, ET ELLE SEULE.
 *
 * La Direction y entrait jusqu'au 2026-09-25, « parce qu'elle arbitre le
 * portefeuille applicatif ». Le donneur d'ordre l'en a retirée : le module est
 * l'outil de ceux qui livrent (applications, remontées, documentation du dépôt,
 * mises en production). Les notifications de livraison vont au sponsor et au
 * chef de projet (`dossierStakeholders`) : la Direction n'en reçoit qu'à ce
 * titre-là, sur ses propres projets.
 *
 * `roles` reste dans la signature : tous les appelants le passent, et la
 * prochaine règle qui voudra un rôle global le trouvera là, au lieu de rouvrir
 * trente appels. Ne pas y remettre `direction` sans relire cette décision.
 */
export function canViewDevModule(_roles, jobFunctionCode) {
    return jobFunctionCode === 'developpeur';
}
/** Porter la fonction — traiter les remontées, prendre un ticket. */
export function isDeveloper(jobFunctionCode) {
    return jobFunctionCode === 'developpeur';
}
/**
 * Travailler sur GitHub depuis l'outil : issues, jalons, pull requests, commits
 * (docs/MODULE5-GITHUB.md).
 *
 * LA FONCTION SEULE (décision D1 du 25/09/2026). Le module entier l'est
 * désormais aussi, mais le prédicat reste À PART : le jour où le module
 * rouvrirait à un autre public, GitHub ne suivrait pas sans qu'on le décide.
 * Ne pas « simplifier » en `canViewDevModule`.
 */
export function canUseGithubWork(jobFunctionCode) {
    return jobFunctionCode === 'developpeur';
}
/**
 * Tenir le RÉFÉRENTIEL des applications (créer une fiche, la rattacher à un
 * projet, désigner un référent).
 *
 * Aux développeurs : c'est le développeur qui sait qu'un dépôt vient de
 * naître, et une fiche qu'il faut demander à quelqu'un d'autre est une fiche
 * qui n'existera pas.
 */
export function canManageApplications(roles, jobFunctionCode) {
    return canViewDevModule(roles, jobFunctionCode);
}
/**
 * SUPPRIMER une fiche d'application — la fonction Développeur.
 *
 * Réservé à la Direction jusqu'au 2026-09-25 ; elle a quitté le module, et le
 * donneur d'ordre a confié la suppression aux développeurs plutôt que de la
 * laisser sans titulaire. Le danger n'a pas changé : la suppression emporte EN
 * CASCADE toutes les remontées de l'application, y compris celles des
 * testeurs. C'est la confirmation qui le porte désormais (elle NOMME ce qui
 * part), et la désactivation (`active = false`) reste proposée en repli.
 *
 * Prédicat à part, et non `isDeveloper` recopié dans la route puis dans
 * l'écran : c'est la seule manière que le bouton et la garde disent la même
 * chose.
 */
export function canDeleteApplication(_roles, jobFunctionCode) {
    return isDeveloper(jobFunctionCode);
}
/**
 * DÉPOSER une remontée.
 *
 * Le testeur d'un projet en est — c'est le cœur de la demande : « les personnes
 * ayant le rôle de Testeur au sein d'un projet pourraient venir implémenter des
 * enregistrements au sein de ce module pour permettre aux développeurs un
 * retour immédiat ». Son droit se vérifie donc PAR PROJET (`standing`), comme
 * partout depuis 0045, et non sur un rôle global : `testerProjectIds` est la
 * liste des projets où il porte l'étiquette.
 *
 * Un développeur ou la Direction déposent sans condition de projet : ils voient
 * déjà tout le module.
 */
export function canReportTicket(roles, jobFunctionCode, testerProjectIds = []) {
    return canViewDevModule(roles, jobFunctionCode) || testerProjectIds.length > 0;
}
/**
 * TRAITER une remontée : changer son statut, l'affecter, écrire la résolution.
 *
 * Réservé à ceux qui corrigent. Le testeur, lui, commente et rouvre — il ne
 * décrète pas qu'un bug est corrigé, sans quoi le statut « À tester » n'aurait
 * plus de sens : c'est précisément la passe de main entre les deux.
 */
export function canTriageTicket(roles, jobFunctionCode) {
    return canViewDevModule(roles, jobFunctionCode);
}
//# sourceMappingURL=roles.js.map